import React from 'react';
import { cn } from '@/utils/cn';

/**
 * Select Component
 * 
 * A customizable select dropdown component that provides consistent styling
 * and behavior across the application. Supports validation states and
 * helper text.
 * 
 * Features:
 * - Consistent theme integration
 * - Error state styling
 * - Helper text support
 * - Placeholder options
 * - Accessibility support
 * 
 * @example
 * ```tsx
 * <Select
 *   options={[
 *     { value: 'option1', label: 'Option 1' },
 *     { value: 'option2', label: 'Option 2' }
 *   ]}
 *   placeholder="Select an option"
 *   error={!!errors.field}
 *   helperText={errors.field}
 * />
 * ```
 */

/**
 * Select option interface
 * 
 * Defines the structure of individual options within a select dropdown.
 */
export interface SelectOption {
  /** Value of the option */
  value: string;
  /** Display label for the option */
  label: string;
  /** Greyed-out option the user cannot pick */
  disabled?: boolean;
}

/**
 * Select component props interface
 * 
 * Extends HTML select attributes and includes select-specific props
 * for styling, validation, and option management.
 */
export interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  /** Array of options to display in the dropdown */
  options: SelectOption[];
  /** Optional placeholder text */
  placeholder?: string;
  /** Whether the select is in an error state */
  error?: boolean;
  /** Helper text to display below the select */
  helperText?: string;
}

/**
 * Select component implementation
 * 
 * Renders a customizable select dropdown with consistent styling,
 * validation states, and accessibility features.
 */
export const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
  ({ className, options, placeholder, error, helperText, ...props }, ref) => {
    return (
      <div className="w-full">
        {/* Select dropdown with validation styling */}
        <select
          className={cn(
            'w-full px-3 py-2 border rounded-lg bg-[var(--color-surface-bg)] text-[var(--color-surface-text)] border-[var(--color-border)] focus:outline-none focus:ring-2 focus:ring-[var(--color-primary-bg)] focus:border-transparent',
            error && 'border-red-500 focus:ring-red-500',
            className
          )}
          ref={ref}
          {...props}
        >
          {/* Placeholder option */}
          {placeholder && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {/* Dynamic options */}
          {options.map((option) => (
            <option key={option.value} value={option.value} disabled={option.disabled}>
              {option.label}
            </option>
          ))}
        </select>
        {/* Helper text with error styling */}
        {helperText && (
          <p className={cn(
            'mt-1 text-sm',
            error ? 'text-red-500' : 'text-[var(--color-surface-text)]'
          )}>
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

Select.displayName = 'Select';
